import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Home,
  LayoutGrid,
  CalendarCheck,
  BadgePercent,
  Info,
  Phone,
  CircleHelp,
  BriefcaseBusiness,
  Users,
  ShieldCheck,
  Star,
  Search,
  UserRound,
  CalendarDays,
  CheckCircle2,
  Target,
  Eye,
  MapPin,
  ArrowRight,
} from "lucide-react";
import clickNidoLogo from "../images/stage_01/ClickNido_Logo.png";
import aboutHeroImg from "../images/stage_01/about_us_team.png";
import electricianProImg from "../images/stage_01/Electrican_labour.png";
import cleanerProImg from "../images/stage_01/cleaning_labour.png";
// PLACEHOLDER — caretaker_labour.png/.jpg was never added to stage_01 (no
// Electrican_labour/cleaning_labour/kitchen_labour-style counterpart exists
// for Caretaker); using the closest general caretaker photo until a
// dedicated one is supplied.
import caretakerProImg from "../images/stage_01/caretaker_service_general.png";
import kitchenProImg from "../images/stage_01/kitchen_labour.png";

const sidebarLinks = [
  { label: "Home", icon: Home },
  { label: "All Services", icon: LayoutGrid },
  { label: "My Bookings", icon: CalendarCheck },
  { label: "Offers", icon: BadgePercent },
  { label: "About Us", icon: Info, active: true },
  { label: "Contact Us", icon: Phone },
  { label: "Help Center", icon: CircleHelp },
];

const stats = [
  { icon: BriefcaseBusiness, value: "10,000+", label: "Jobs Completed", color: "#6d5efc" },
  { icon: Users, value: "2,500+", label: "Happy Customers", color: "#6bc58f" },
  { icon: ShieldCheck, value: "500+", label: "Verified Experts", color: "#58c39a" },
  { icon: Star, value: "4.9 / 5", label: "Customer Rating", color: "#f2b94b" },
];

const categories = [
  {
    title: "Home Services",
    icon: "🏠",
    color: "#6e63ff",
    path: "/services/instant-home-services",
    items: ["Cleaning", "Plumbing", "Electrician", "AC Service", "Carpenter", "Painting", "Pest Control"],
  },
  {
    title: "Technical Services",
    icon: "🖥️",
    color: "#5b6cff",
    path: "/services/technical-services",
    items: ["Laptop Repair", "Desktop Repair", "Printer Service", "WiFi Setup", "CCTV Installation", "Smart Home Setup", "Software Support"],
  },
  {
    title: "Caretaker Services",
    icon: "🧑‍⚕️",
    color: "#d06ce0",
    path: "/services/caretaker-services",
    items: ["Baby Care", "Elder Care", "Patient Care", "Nursing Assistant", "Pet Care"],
  },
  {
    title: "Outdoor Services",
    icon: "🌿",
    color: "#62c37f",
    path: "/services/outdoor-services",
    items: ["Gardening", "Landscaping", "Security Guard", "House Shifting Help", "Waste Removal"],
  },
  {
    title: "Transport Services",
    icon: "🚚",
    color: "#f2a84b",
    path: "/services/transport-services",
    items: ["Mini Truck", "Packers & Movers", "Bike Transport", "Courier", "Driver On Demand", "Airport Pickup"],
  },
  {
    title: "Special Services",
    icon: "⭐",
    color: "#b86bf4",
    path: "/services/special-services",
    items: ["Deep Cleaning", "1BHK Cleaning", "2BHK Cleaning", "Rapid Pest Function", "Cleaning"],
  },
];

const whyChoose = [
  {
    title: "Verified Professionals",
    desc: "All experts are background checked and verified",
    color: "#6d5efc",
  },
  {
    title: "Quality Assurance",
    desc: "We ensure best quality service every time",
    color: "#58c39a",
  },
  {
    title: "Transparent Pricing",
    desc: "No hidden charges. What you see is what you pay",
    color: "#7b6ef6",
  },
  {
    title: "Easy Booking",
    desc: "Book in less than 60 seconds",
    color: "#69c590",
  },
  {
    title: "On-time Service",
    desc: "Punctual and reliable service delivery",
    color: "#7f74ff",
  },
  {
    title: "24/7 Support",
    desc: "We’re here to help you anytime",
    color: "#5fc58e",
  },
];

const steps = [
  { no: "1", title: "Choose Service", desc: "Select the service you need from our wide range of categories", icon: Search },
  { no: "2", title: "Select Expert", desc: "We'll match you with the best professional suitable for you", icon: UserRound },
  { no: "3", title: "Pick a Time", desc: "Choose a convenient date and time for your service", icon: CalendarDays },
  { no: "4", title: "Service Delivered", desc: "Relax while we take care of the rest with quality service", icon: CheckCircle2 },
];

const locations = ["Mysore", "Bangalore"];

const professionals = [
  {
    name: "Rahul Kumar",
    role: "Electrician",
    rating: "4.9",
    jobs: "120 Jobs",
    image: electricianProImg,
  },
  {
    name: "Anita Sharma",
    role: "Cleaner",
    rating: "4.8",
    jobs: "95 Jobs",
    image: cleanerProImg,
  },
  {
    name: "Sneha Reddy",
    role: "Caretaker",
    rating: "4.9",
    jobs: "80 Jobs",
    image: caretakerProImg,
  },
  {
    name: "Pooja Verma",
    role: "Caretaker",
    rating: "4.8",
    jobs: "60 Jobs",
    image: caretakerProImg,
  },
  {
    name: "Divya Menon",
    role: "Kitchen Cleaning",
    rating: "4.7",
    jobs: "60 Jobs",
    image: kitchenProImg,
  },
];

const testimonials = [
  {
    text: "Booked deep cleaning for my 2BHK. The team was on time and did an excellent job!",
    name: "Priya S.",
    city: "Bangalore",
  },
  {
    text: "Very professional and polite caretaker. My parents are in safe hands.",
    name: "Arjun K.",
    city: "Mysore",
  },
  {
    text: "Quick booking, transparent pricing and amazing customer support.",
    name: "Neha T.",
    city: "Hyderabad",
  },
];

const AboutUs = () => {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <div style={styles.shell}>
        {/* Sidebar */}
        <aside style={styles.sidebar}>
          <div style={styles.logoRow}>
            <img src={clickNidoLogo} alt="ClickNido" style={styles.logoImg} />
          </div>

          <div style={styles.sideNav}>
            {sidebarLinks.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.label}
                  style={{
                    ...styles.sideLink,
                    ...(item.active ? styles.sideLinkActive : {}),
                  }}
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </div>

          <div style={styles.appCard}>
            <h4 style={styles.appCardTitle}>Get Our App</h4>
            <p style={styles.appCardText}>
              Faster bookings, easy tracking and exclusive offers.
            </p>

            <div style={styles.storeColumn}>
              <button style={styles.storeBtn}>Get it on Google Play</button>
              <button style={styles.storeBtn}>Download on the App Store</button>
            </div>

            <div style={styles.mockPhone}>
              <div style={styles.phoneNotch}></div>
              <div style={styles.phoneScreen}>
                <div style={styles.mockText1}>Hello, Priya 👋</div>
                <div style={styles.mockText2}>What service do you need?</div>
                <div style={styles.mockSearch}>Search service...</div>
                <div style={styles.mockIcons}>
                  <span>🧹</span>
                  <span>🔧</span>
                  <span>❄️</span>
                  <span>⚡</span>
                </div>
              </div>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main style={styles.main}>
          <div style={styles.breadcrumb}>Home &nbsp;›&nbsp; About Us</div>

          <section style={styles.heroRow}>
            <div style={styles.heroContent}>
              <h1 style={styles.heroTitle}>About Us</h1>
              <h2 style={styles.heroHeading}>
                We’re here to make
                <br />
                your <span style={styles.highlight}>life easier</span>
              </h2>
              <p style={styles.heroText}>
                ServiceHub connects you with trusted and verified professionals
                for all your home and business needs. Quality service, on time,
                every time.
              </p>
              <button style={styles.primaryBtn} onClick={() => navigate("/")}>
                Book a Service <ArrowRight size={16} />
              </button>
            </div>

            <div style={styles.heroImageWrap}>
              <img
                src={aboutHeroImg}
                alt="ClickNido service professionals"
                style={styles.heroImage}
              />
            </div>
          </section>

          <section style={styles.statsBar}>
            {stats.map((item) => {
              const Icon = item.icon;
              return (
                <div key={item.label} style={styles.statItem}>
                  <div style={{ ...styles.statIcon, color: item.color }}>
                    <Icon size={18} />
                  </div>
                  <div>
                    <div style={styles.statValue}>{item.value}</div>
                    <div style={styles.statLabel}>{item.label}</div>
                  </div>
                </div>
              );
            })}
          </section>

          <section style={styles.section}>
            <h3 style={styles.centerTitle}>Our Service Categories</h3>
            <div style={styles.categoryGrid}>
              {categories.map((item) => (
                <div key={item.title} style={styles.categoryCard}>
                  <div style={{ ...styles.categoryIcon, color: item.color }}>
                    {item.icon}
                  </div>
                  <h4 style={styles.categoryTitle}>{item.title}</h4>
                  <ul style={styles.categoryList}>
                    {item.items.map((point) => (
                      <li key={point}>{point}</li>
                    ))}
                  </ul>
                  <button
                    style={styles.viewBtn}
                    onClick={() => navigate(item.path)}
                  >
                    View All <ArrowRight size={14} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          <section style={styles.twoGrid}>
            <div style={styles.panelCard}>
              <h3 style={styles.panelTitle}>Why Choose ServiceHub?</h3>
              <div style={styles.whyGrid}>
                {whyChoose.map((item) => (
                  <div key={item.title} style={styles.whyItem}>
                    <div
                      style={{
                        ...styles.whyDot,
                        background: `${item.color}18`,
                        color: item.color,
                      }}
                    >
                      <ShieldCheck size={16} />
                    </div>
                    <div>
                      <div style={styles.whyTitle}>{item.title}</div>
                      <div style={styles.whyDesc}>{item.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.panelCard}>
              <h3 style={styles.panelTitle}>How It Works</h3>
              <div style={styles.stepsGrid}>
                {steps.map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.no} style={styles.stepCard}>
                      <div style={styles.stepIcon}>
                        <Icon size={20} color="#6d5efc" />
                      </div>
                      <div style={styles.stepNo}>{item.no}</div>
                      <div style={styles.stepTitle}>{item.title}</div>
                      <div style={styles.stepDesc}>{item.desc}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <section style={styles.twoGrid}>
            <div style={styles.missionCard}>
              <h3 style={styles.panelTitle}>Our Mission & Vision</h3>
              <div style={styles.missionGrid}>
                <div style={styles.missionBlock}>
                  <div style={styles.missionIcon}>
                    <Target size={18} color="#6d5efc" />
                  </div>
                  <div style={styles.missionHeading}>Our Mission</div>
                  <p style={styles.missionText}>
                    To simplify lives by providing reliable, affordable and
                    high-quality services through a trusted network of
                    professionals.
                  </p>
                </div>
                <div style={styles.missionBlock}>
                  <div style={styles.missionIcon}>
                    <Eye size={18} color="#6d5efc" />
                  </div>
                  <div style={styles.missionHeading}>Our Vision</div>
                  <p style={styles.missionText}>
                    To become India’s most trusted and preferred service
                    platform, creating happier homes and better lives.
                  </p>
                </div>
              </div>
            </div>

            <div style={styles.missionCard}>
              <h3 style={styles.panelTitle}>Areas We Serve</h3>
              <p style={styles.serveText}>
                We are growing fast and already serving in multiple cities near
                you.
              </p>
              <div style={styles.locationGrid}>
                {locations.map((city) => (
                  <div key={city} style={styles.locationChip}>
                    <MapPin size={13} color="#6d5efc" />
                    {city}
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section style={styles.section}>
            <div style={styles.sectionHeadRow}>
              <h3 style={styles.sectionTitle}>Our Trusted Professionals</h3>
              <button style={styles.inlineLink}>
                View All Professionals <ArrowRight size={14} />
              </button>
            </div>

            <div style={styles.proGrid}>
              {professionals.map((item) => (
                <div key={item.name} style={styles.proCard}>
                  <img src={item.image} alt={item.name} style={styles.proImage} />
                  <div style={styles.proBody}>
                    <div style={styles.proName}>{item.name}</div>
                    <div style={styles.proRole}>{item.role}</div>
                    <div style={styles.proMeta}>⭐ {item.rating} ({item.jobs})</div>
                    <div style={styles.verifiedTag}>● Verified</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={styles.section}>
            <div style={styles.sectionHeadRow}>
              <h3 style={styles.sectionTitle}>What Our Customers Say</h3>
              <button style={styles.inlineLink}>
                View All Reviews <ArrowRight size={14} />
              </button>
            </div>

            <div style={styles.reviewGrid}>
              {testimonials.map((item) => (
                <div key={item.name} style={styles.reviewCard}>
                  <div style={styles.quote}>“</div>
                  <p style={styles.reviewText}>{item.text}</p>
                  <div style={styles.reviewUser}>
                    <div>
                      <div style={styles.reviewName}>{item.name}</div>
                      <div style={styles.reviewCity}>{item.city}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section style={styles.bottomCta}>
            <div>
              <div style={styles.bottomCtaTitle}>
                Ready to experience the best service?
              </div>
              <div style={styles.bottomCtaText}>
                Book trusted professionals in just a few clicks.
              </div>
            </div>
            <button style={styles.bottomCtaBtn} onClick={() => navigate("/")}>
              Book a Service <ArrowRight size={16} />
            </button>
          </section>

          <section style={styles.trustBar}>
            <div style={styles.trustItem}>🛡️ 100% Safe & Secure</div>
            <div style={styles.trustItem}>🏷️ No Hidden Charges</div>
            <div style={styles.trustItem}>✨ Best Quality Service</div>
            <div style={styles.trustItem}>⏱️ Quick & Easy Booking</div>
          </section>
        </main>
      </div>
    </div>
  );
};

const styles = {
  page: {
    background: "#f5f6fb",
    minHeight: "100vh",
    padding: "14px",
    fontFamily:
      "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    color: "#111827",
  },
  shell: {
    display: "grid",
    gridTemplateColumns: "240px 1fr",
    gap: "16px",
    maxWidth: "1600px",
    margin: "0 auto",
  },
  sidebar: {
    background: "#fff",
    borderRadius: "22px",
    padding: "26px 18px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 28px)",
  },
  logoRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "28px",
    padding: "0 6px",
  },
  logoImg: {
    height: "36px",
    width: "auto",
    objectFit: "contain",
    borderRadius: "6px",
  },
  sideNav: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    marginBottom: "26px",
  },
  sideLink: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    border: "none",
    background: "transparent",
    textAlign: "left",
    padding: "12px 12px",
    borderRadius: "12px",
    color: "#3b4261",
    fontSize: "14px",
    fontWeight: 600,
    cursor: "pointer",
  },
  sideLinkActive: {
    background: "#f0eeff",
    color: "#6d5efc",
  },
  appCard: {
    marginTop: "auto",
    background: "#f5f3ff",
    borderRadius: "20px",
    padding: "18px",
  },
  appCardTitle: {
    margin: 0,
    fontSize: "28px",
    fontWeight: 800,
    color: "#25305e",
    marginBottom: "10px",
  },
  appCardText: {
    margin: 0,
    fontSize: "13px",
    color: "#6b7280",
    lineHeight: 1.6,
    marginBottom: "14px",
  },
  storeColumn: {
    display: "grid",
    gap: "10px",
    marginBottom: "16px",
  },
  storeBtn: {
    background: "#111",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    padding: "12px 14px",
    fontWeight: 700,
    fontSize: "13px",
    textAlign: "left",
    cursor: "pointer",
  },
  mockPhone: {
    width: "100%",
    maxWidth: "160px",
    margin: "8px auto 0",
    background: "#111827",
    borderRadius: "24px",
    padding: "6px",
  },
  phoneNotch: {
    width: "74px",
    height: "14px",
    background: "#111827",
    borderRadius: "0 0 10px 10px",
    margin: "0 auto",
  },
  phoneScreen: {
    background: "#fff",
    borderRadius: "18px",
    minHeight: "220px",
    marginTop: "-8px",
    padding: "14px 12px",
  },
  mockText1: {
    fontSize: "10px",
    color: "#6b7280",
    marginBottom: "6px",
  },
  mockText2: {
    fontSize: "13px",
    fontWeight: 700,
    color: "#111827",
    lineHeight: 1.35,
    marginBottom: "10px",
  },
  mockSearch: {
    background: "#f3f4f6",
    borderRadius: "10px",
    padding: "9px 10px",
    fontSize: "10px",
    color: "#9ca3af",
    marginBottom: "12px",
  },
  mockIcons: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "8px",
    fontSize: "18px",
    textAlign: "center",
  },
  main: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  breadcrumb: {
    color: "#7b8198",
    fontSize: "13px",
    fontWeight: 600,
    padding: "8px 6px 0",
  },
  heroRow: {
    display: "grid",
    gridTemplateColumns: "1.1fr 1.45fr",
    gap: "18px",
    alignItems: "stretch",
  },
  heroContent: {
    background: "#fff",
    borderRadius: "22px",
    padding: "34px 34px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
  },
  heroTitle: {
    margin: 0,
    fontSize: "28px",
    fontWeight: 800,
    color: "#1c2148",
    marginBottom: "8px",
  },
  heroHeading: {
    margin: 0,
    fontSize: "58px",
    lineHeight: 1.05,
    fontWeight: 850,
    letterSpacing: "-1.5px",
    color: "#101635",
    marginBottom: "16px",
  },
  highlight: {
    color: "#5f5cff",
  },
  heroText: {
    margin: 0,
    maxWidth: "460px",
    fontSize: "16px",
    lineHeight: 1.75,
    color: "#697186",
    marginBottom: "22px",
  },
  primaryBtn: {
    background: "linear-gradient(90deg, #5d5afe 0%, #6c45ff 100%)",
    color: "#fff",
    border: "none",
    borderRadius: "12px",
    height: "48px",
    padding: "0 20px",
    fontSize: "15px",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: "10px",
    cursor: "pointer",
    boxShadow: "0 12px 24px rgba(103, 88, 255, 0.22)",
  },
  heroImageWrap: {
    background: "#fff",
    borderRadius: "22px",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    minHeight: "360px",
  },
  heroImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
  },
  statsBar: {
    background: "#fff",
    borderRadius: "18px",
    padding: "18px 22px",
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "4px 10px",
  },
  statIcon: {
    width: "42px",
    height: "42px",
    borderRadius: "50%",
    background: "#f7f7ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    fontSize: "16px",
    fontWeight: 800,
    color: "#1d2448",
    marginBottom: "2px",
  },
  statLabel: {
    fontSize: "13px",
    color: "#7b8198",
    fontWeight: 500,
  },
  section: {
    background: "transparent",
  },
  centerTitle: {
    textAlign: "center",
    margin: 0,
    fontSize: "18px",
    fontWeight: 800,
    color: "#242b4f",
    marginBottom: "16px",
  },
  categoryGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
    gap: "14px",
  },
  categoryCard: {
    background: "#fff",
    borderRadius: "18px",
    padding: "18px 16px",
    minHeight: "255px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
  },
  categoryIcon: {
    fontSize: "24px",
    marginBottom: "10px",
  },
  categoryTitle: {
    margin: 0,
    fontSize: "16px",
    fontWeight: 800,
    color: "#263053",
    marginBottom: "10px",
  },
  categoryList: {
    margin: 0,
    paddingLeft: "16px",
    color: "#6e7690",
    fontSize: "13px",
    lineHeight: 1.8,
    minHeight: "140px",
  },
  viewBtn: {
    border: "none",
    background: "transparent",
    color: "#6d5efc",
    fontWeight: 700,
    fontSize: "14px",
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  twoGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  panelCard: {
    background: "#fff",
    borderRadius: "20px",
    padding: "22px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
  },
  panelTitle: {
    margin: 0,
    marginBottom: "18px",
    fontSize: "18px",
    fontWeight: 800,
    color: "#21284d",
  },
  whyGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px 18px",
  },
  whyItem: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-start",
  },
  whyDot: {
    width: "32px",
    height: "32px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  whyTitle: {
    fontSize: "14px",
    fontWeight: 800,
    color: "#283255",
    marginBottom: "4px",
  },
  whyDesc: {
    fontSize: "12.5px",
    lineHeight: 1.55,
    color: "#737b93",
  },
  stepsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: "12px",
  },
  stepCard: {
    textAlign: "center",
  },
  stepIcon: {
    width: "52px",
    height: "52px",
    borderRadius: "50%",
    background: "#f2f0ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 12px",
  },
  stepNo: {
    fontSize: "14px",
    fontWeight: 800,
    color: "#1f264c",
    marginBottom: "6px",
  },
  stepTitle: {
    fontSize: "13px",
    fontWeight: 800,
    color: "#263053",
    marginBottom: "6px",
  },
  stepDesc: {
    fontSize: "12px",
    lineHeight: 1.55,
    color: "#707893",
  },
  missionCard: {
    background: "#fff",
    borderRadius: "20px",
    padding: "22px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    minHeight: "255px",
  },
  missionGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "20px",
    marginTop: "8px",
  },
  missionBlock: {},
  missionIcon: {
    width: "34px",
    height: "34px",
    borderRadius: "50%",
    background: "#f2f0ff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "10px",
  },
  missionHeading: {
    fontSize: "15px",
    fontWeight: 800,
    color: "#273154",
    marginBottom: "8px",
  },
  missionText: {
    margin: 0,
    fontSize: "13px",
    lineHeight: 1.8,
    color: "#6f7892",
  },
  serveText: {
    margin: 0,
    fontSize: "13px",
    lineHeight: 1.75,
    color: "#6f7892",
    marginBottom: "16px",
  },
  locationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: "12px",
  },
  locationChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 12px",
    borderRadius: "12px",
    background: "#f8f8fe",
    color: "#3c4462",
    fontSize: "13px",
    fontWeight: 600,
  },
  locationsBtn: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontSize: "14px",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  sectionHeadRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "16px",
    marginBottom: "14px",
  },
  sectionTitle: {
    margin: 0,
    fontSize: "18px",
    fontWeight: 800,
    color: "#21284d",
  },
  inlineLink: {
    background: "transparent",
    border: "none",
    color: "#6d5efc",
    fontSize: "14px",
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
  },
  proGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: "14px",
  },
  proCard: {
    background: "#fff",
    borderRadius: "16px",
    overflow: "hidden",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
  },
  proImage: {
    width: "100%",
    aspectRatio: "1 / 1",
    objectFit: "cover",
    display: "block",
  },
  proBody: {
    padding: "12px",
  },
  proName: {
    fontSize: "14px",
    fontWeight: 800,
    color: "#20274b",
    marginBottom: "4px",
  },
  proRole: {
    fontSize: "12px",
    color: "#707893",
    marginBottom: "6px",
  },
  proMeta: {
    fontSize: "12px",
    color: "#4f5978",
    marginBottom: "6px",
  },
  verifiedTag: {
    fontSize: "11px",
    color: "#1ca463",
    fontWeight: 700,
  },
  reviewGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: "14px",
  },
  reviewCard: {
    background: "#fff",
    borderRadius: "16px",
    padding: "18px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
  },
  quote: {
    fontSize: "36px",
    lineHeight: 1,
    color: "#6d5efc",
    marginBottom: "4px",
  },
  reviewText: {
    margin: 0,
    fontSize: "13px",
    lineHeight: 1.8,
    color: "#555f7d",
    minHeight: "72px",
  },
  reviewUser: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "16px",
  },
  reviewAvatar: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    objectFit: "cover",
  },
  reviewName: {
    fontSize: "13px",
    fontWeight: 800,
    color: "#22294d",
  },
  reviewCity: {
    fontSize: "12px",
    color: "#737b93",
  },
  bottomCta: {
    background: "linear-gradient(90deg, #6d5efc 0%, #5e63ff 50%, #7a5df6 100%)",
    borderRadius: "18px",
    padding: "22px 26px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "18px",
    color: "#fff",
  },
  bottomCtaTitle: {
    fontSize: "24px",
    fontWeight: 800,
    marginBottom: "4px",
  },
  bottomCtaText: {
    fontSize: "14px",
    color: "rgba(255,255,255,0.88)",
  },
  bottomCtaBtn: {
    background: "#fff",
    color: "#5f58ff",
    border: "none",
    borderRadius: "12px",
    height: "46px",
    padding: "0 18px",
    fontSize: "14px",
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  trustBar: {
    background: "#fff",
    borderRadius: "16px",
    padding: "14px 18px",
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "12px",
    boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 28px rgba(16,24,40,0.04)",
    marginBottom: "10px",
  },
  trustItem: {
    fontSize: "13px",
    color: "#4a5574",
    fontWeight: 700,
    textAlign: "center",
  },
};

export default AboutUs;
